import React, { useEffect } from 'react';
import styles from './Filter.module.scss';

import DialogWrapper from 'components/DialogComponents/DialogWrapper';
import { FilterActions, FilterHeader } from 'components/FilterComponentsV2';
import {
  FormDayPicker,
  FormLabel,
  FormMultipleUserSearch,
  FormMultipleLooseItemSearch
} from 'components/FormComponents';
import FormAutoMultiSelect from 'components/FormComponents/FormAutoMultiSelect/FormAutoMultiSelect';
import FormCheckbox from 'components/FormComponents/FormCheckbox/FormCheckbox';

import { FormProvider, useForm } from 'react-hook-form';

import { useReportSelector } from 'hooks/ReportList';
import { useMobileViewport } from 'hooks/useMobileViewport';

import clsx from 'clsx';

import { splitLocationsByStatus } from '../helpers';

export default function Filter({ open, setOpen, onApply, filter, reportType }) {
  const isMobile = useMobileViewport();

  const methods = useForm({
    defaultValues: {
      locationIds: [],
      locationJobNumberIds: [],
      projectIds: [],
      assignedUserIds: [],
      generalReportTemplateIds: [],
      createdUserIds: [],
      createdUserList: [],
      looseItemIds: [],
      looseItemList: [],
      assetPrefixIds: [],
      dateFrom: null,
      dateTo: null,
      showAssigned: false,
      showNotSubmitted: false,
      showWithInactiveLocations: false
    },
    mode: 'onChange'
  });
  const { getValues, reset, watch, setValue } = methods;

  const { filterCriteria = {} } = useReportSelector();

  const { locations, generalReportTemplate, projects, users, assetPrefixes } = filterCriteria;
  const { activeLocations, inactiveLocations } = splitLocationsByStatus(locations);

  useEffect(() => {
    if (open) {
      reset(filter.filters);
    }
  }, [open]);

  const closePopup = () => setOpen(false);

  const applyFilter = () => {
    onApply(getValues());
    closePopup();
  };

  const clearFilter = () => {
    const defaultState = {
      ...getValues(),
      locationIds: [],
      locationJobNumberIds: [],
      projectIds: [],
      assignedUserIds: [],
      generalReportTemplateIds: [],
      dateFrom: null,
      dateTo: null,
      showAssigned: false,
      showNotSubmitted: false,
      showWithInactiveLocations: false,
      createdUserList: [],
      createdUserIds: [],
      looseItemIds: [],
      looseItemList: [],
      assetPrefixIds: []
    };
    reset(defaultState);
  };

  const switchLocations = () => {
    setValue('locationIds', []);
    setValue('locationJobNumberIds', []);
  };

  const dateFromWatcher = watch('dateFrom');
  const dateToWatcher = watch('dateTo');
  const showWithInactiveLocationsWatcher = watch('showWithInactiveLocations');

  const locationMenuItems =
    reportType === 'Location'
      ? (showWithInactiveLocationsWatcher ? inactiveLocations : activeLocations) ?? []
      : locations ?? [];

  return (
    <DialogWrapper open={open} onClose={closePopup}>
      <div className={styles.dialog}>
        {isMobile && <FilterHeader title="Filters" onBack={closePopup} />}
        <FormProvider {...methods}>
          <div className={styles.form}>
            <div className={styles.form__block}>
              <div className={styles.form__block_cell}>
                <FormLabel>Location</FormLabel>
                <FormAutoMultiSelect
                  name="locationIds"
                  menuItems={locationMenuItems}
                  options={{
                    label: 'siteCode',
                    disableByObjectTracker: true,
                    disableLabel: true
                  }}
                />
              </div>

              {['Asset', 'Location'].includes(reportType) && (
                <>
                  <div className={styles.form__block_cell}>
                    <FormLabel>Job Number</FormLabel>
                    <FormAutoMultiSelect
                      name="locationJobNumberIds"
                      menuItems={locationMenuItems}
                      options={{
                        label: 'locationJobNumber',
                        disableByObjectTracker: true,
                        disableLabel: true
                      }}
                    />
                  </div>
                </>
              )}

              {reportType === 'Location' && (
                <div className={clsx(styles.form__block_cell, styles.checkbox)}>
                  <FormLabel>Show inactive location reports only</FormLabel>
                  <FormCheckbox
                    name="showWithInactiveLocations"
                    onSelectTriggered={switchLocations}
                  />
                </div>
              )}

              {reportType === 'General' && (
                <>
                  <div className={styles.form__block_cell}>
                    <FormLabel>Asset Prefixes</FormLabel>
                    <FormAutoMultiSelect
                      name="assetPrefixIds"
                      menuItems={assetPrefixes || []}
                      options={{ label: 'name', disableByObjectTracker: true, disableLabel: true }}
                    />
                  </div>
                  <div className={styles.form__block_cell}>
                    <FormLabel>Loose Item</FormLabel>
                    <FormMultipleLooseItemSearch
                      name="looseItemIds"
                      listName="looseItemList"
                      getQueryOptions={(searchQuery) => ({ filters: { searchQuery } })}
                    />
                  </div>
                </>
              )}

              <div className={styles.form__block_cell}>
                <FormLabel>Report name</FormLabel>
                <FormAutoMultiSelect
                  name="generalReportTemplateIds"
                  menuItems={generalReportTemplate || []}
                  options={{
                    label: 'name',
                    disableByObjectTracker: true,
                    disableLabel: true
                  }}
                />
              </div>

              {reportType === 'Location' && (
                <div className={styles.form__block_cell}>
                  <FormLabel>Project</FormLabel>
                  <FormAutoMultiSelect
                    name="projectIds"
                    menuItems={projects || []}
                    options={{
                      labelType: 'project',
                      disableByObjectTracker: true,
                      disableLabel: true
                    }}
                  />
                </div>
              )}

              <div className={styles.form__block_cell}>
                <FormLabel>Assignee</FormLabel>
                <FormAutoMultiSelect
                  name="assignedUserIds"
                  menuItems={users || []}
                  options={{
                    labelType: 'user',
                    disableByObjectTracker: true,
                    disableLabel: true
                  }}
                />
              </div>
            </div>

            <div className={styles.form__block}>
              <div className={styles.form__block_cell}>
                <FormLabel>Created by</FormLabel>
                <FormMultipleUserSearch
                  name="createdUserIds"
                  listName="createdUserList"
                  getQueryOptions={(searchQuery) => ({
                    pagination: { limit: 40, page: 1 },
                    filters: { shouldIncludeCurrentUser: true, searchQuery }
                  })}
                />
              </div>
              <div className={styles.form__block_cell}>
                <FormLabel>Created from</FormLabel>
                <FormDayPicker name="dateFrom" max={dateToWatcher} />
              </div>
              <div className={styles.form__block_cell}>
                <FormLabel>Created to</FormLabel>
                <FormDayPicker name="dateTo" min={dateFromWatcher} />
              </div>

              <div className={clsx(styles.form__block_cell, styles.checkbox)}>
                <FormLabel>Show assigned</FormLabel>
                <FormCheckbox name="showAssigned" />
              </div>
              <div className={clsx(styles.form__block_cell, styles.checkbox)}>
                <FormLabel>Show only not submitted</FormLabel>
                <FormCheckbox name="showNotSubmitted" />
              </div>
            </div>
          </div>
        </FormProvider>
        <FilterActions onClear={clearFilter} onCancel={closePopup} onApply={applyFilter} />
      </div>
    </DialogWrapper>
  );
}
